import {
  GetterTree, Module, MutationTree, ActionTree, ActionContext,
} from 'vuex'
import { RootState } from '..'
import { RestUser } from '../../rest/user'
import { RestFile } from '../../rest/file'
import { Grant } from '@/ts/interfaces/grant'
import { PollAnswer } from '@/ts/interfaces/poll-answer'
import { User } from '@/ts/interfaces/user'
import { SpotifyClient } from '@/rest/spotify-client'
import { QuizAnswer } from '@/ts/interfaces/quiz-answer'
import { apple } from '@/services'
import { RestError } from '@/rest/base'
import { cms } from '@/cms'

const restUser = new RestUser()
const restFile = new RestFile()

export interface UserState {
  id: number
  name: string
  displayName: string
  email: string
  countryId: string
  premium: boolean
  profileSticker: string | null
  profilePicture: string | null
  token: string
  role: string
  canChat: boolean
  isSubscribed: boolean
  localKey: string
  serviceToken: string
  serviceTokenExpires: number
  tracksVoted: string[]
  grants: Grant[]
  pollAnswers: PollAnswer[]
  quizAnswers: QuizAnswer[]
  users: User[]
  service: string|null
}

const userState: UserState = {
  id: 0,
  name: '',
  displayName: '',
  email: '',
  premium: true,
  countryId: '',
  profileSticker: null,
  profilePicture: null,
  token: '',
  role: '',
  canChat: true,
  isSubscribed: false,
  localKey: '',
  serviceToken: '',
  serviceTokenExpires: 0,
  tracksVoted: [],
  grants: [],
  pollAnswers: [],
  quizAnswers: [],
  users: [],
  service: null,
}

const mutations: MutationTree<UserState> = {
  SET_USER_ID(state: UserState, id) {
    state.id = id
  },
  SET_USER_NAME(state: UserState, name) {
    state.name = name
  },
  SET_USER_DISPLAY_NAME(state: UserState, displayName) {
    state.displayName = displayName
  },
  SET_USER_EMAIL(state: UserState, email) {
    state.email = email
  },
  SET_USER_PREMIUM(state: UserState, premium) {
    state.premium = premium
  },
  SET_USER_COUNTRY_ID(state: UserState, countryId) {
    state.countryId = countryId
  },
  SET_USER_PROFILE_STICKER(state: UserState, profileSticker) {
    state.profileSticker = profileSticker
  },
  SET_USER_PROFILE_PICTURE(state: UserState, profilePicture) {
    state.profilePicture = profilePicture
  },
  SET_USER_TOKEN(state: UserState, token) {
    state.token = token
  },
  SET_USER_ROLE(state: UserState, role) {
    state.role = role
  },
  SET_USER_CAN_CHAT(state: UserState, canChat) {
    state.canChat = canChat
  },
  SET_USER_IS_SUBSCRIBED(state: UserState, isSubscribed) {
    state.isSubscribed = isSubscribed
  },
  SET_USER_LOCAL_KEY(state: UserState, localKey) {
    state.localKey = localKey
  },
  SET_USER_SERVICE_TOKEN(state: UserState, serviceToken) {
    state.serviceToken = serviceToken
  },
  SET_USER_SERVICE_TOKEN_EXPIRES(state: UserState, serviceTokenExpires) {
    state.serviceTokenExpires = serviceTokenExpires
  },
  SET_USER_TRACKS_VOTED(state: UserState, tracksVoted) {
    state.tracksVoted = tracksVoted
  },
  SET_USER_GRANTS(state: UserState, grants) {
    state.grants = grants
  },
  SET_USER_POLL_ANSWERS(state: UserState, pollAnswers) {
    state.pollAnswers = pollAnswers
  },
  SET_USER_QUIZ_ANSWERS(state: UserState, quizAnswers: QuizAnswer[]) {
    state.quizAnswers = quizAnswers
  },
  SET_USER_USERS(state: UserState, users) {
    state.users = users
  },
  SET_USER_SERVICE(state: UserState, service: string|null) {
    state.service = service
  },
}

const actions: ActionTree<UserState, any> = {
  async USER_INIT(ctx: ActionContext<UserState, RootState>): Promise<void> {
    ctx.commit('SET_USER_LOCAL_KEY', `${ctx.getters.partyId}_party`) // TODO: Why? This can be computed on the fly

    // Check if we are in oauth callback
    const queryParams = new URLSearchParams(window.location.search)
    let accessToken = queryParams.get('access_token') || null
    const serviceToken = queryParams.get('service_token') || null
    const serviceExpires = queryParams.get('service_expires_at') || null
    const authService = queryParams.get('auth_service') || null
    const error = queryParams.get('error') || null

    let newLogin = false
    let authenticated = false

    if (error && authService) {
      cms.tracking.trackCustomer('login_fail', { method: authService, error_message: error })
    } else if (accessToken && serviceToken && serviceExpires && authService) {
      newLogin = true
      ctx.commit('SET_USER_TOKEN', accessToken)
      ctx.commit('SET_USER_SERVICE_TOKEN', serviceToken)
      ctx.commit('SET_USER_SERVICE_TOKEN_EXPIRES', parseInt(serviceExpires, 10))
      // ctx.commit('SET_USER_SERVICE', authService)
      localStorage.setItem(ctx.getters.userLocalAccessKey, accessToken)
      localStorage.setItem(ctx.getters.userLocalService, authService) // TODO: Should not set this, should be part of loaded user object
      // remove these params from URL with reset state
      queryParams.delete('access_token')
      queryParams.delete('service_token')
      queryParams.delete('service_expires_at')
      queryParams.delete('auth_service')
      const newUrl = new URL(window.location.href)
      newUrl.search = queryParams.toString()
      window.history.replaceState({}, document.title, newUrl.toString())
    } else {
      // Check for token in local storage
      accessToken = localStorage.getItem(ctx.getters.userLocalAccessKey)
      if (accessToken) {
        ctx.commit('SET_USER_TOKEN', accessToken)
      }
    }

    if (!ctx.state.token) {
      // We are not logged in, nothing more to do
      return
    }
    let signedUp = false
    try {
      await ctx.dispatch('USER_PARTY_LOAD')
      signedUp = true
      authenticated = true
    } catch (err) {
      if ((err as RestError).status === 401) {
        // Clear all login state if token is invalid
        await ctx.dispatch('USER_LOGOUT')
      } else if ((err as RestError).status === 403) {
        // Logged in but not signed up yet, let's check if system user loads
        console.debug('403: User not signed up yet')
      } else {
        throw (err) // Undefined error
      }
    }
    // Try to load the system user if we are not signed up
    if (!signedUp) {
      try {
        await ctx.dispatch('USER_SYSTEM_LOAD')
        authenticated = true
      } catch (err) {
        if ((err as RestError).status === 401) {
        // Clear all login state if token is invalid
          await ctx.dispatch('USER_LOGOUT')
        } else {
          throw (err) // Undefined error
        }
      }
    }
    if (authenticated && newLogin) {
      cms.tracking.trackCustomer('login_success', { method: ctx.getters.userService, email: ctx.getters.userEmail })
    }
  },
  async USER_LOAD_ALL(ctx: ActionContext<UserState, RootState>): Promise<void> {
    const result = await restUser.getAllBlocked(ctx.getters.userToken, ctx.getters.partyId)
    ctx.commit('SET_USER_USERS', result.items)
  },
  async USER_LOAD_GRANTS(ctx: ActionContext<UserState, RootState>): Promise<void> {
    const result = await restUser.getGrants(ctx.getters.partyId)
    ctx.commit('SET_USER_GRANTS', result)
  },
  async USER_SET_GRANT(ctx: ActionContext<UserState, RootState>, p: { serviceId: string | null, token: string | null, service: string, role: string, displayName: string, picture: string }): Promise<void> {
    await restUser.grant(
      ctx.getters.partyId,
      // TODO: Unify payload type. Don't repeat param names here
      {
        service_id: p.serviceId,
        service: p.service,
        token: p.token,
        role: p.role,
        display_name: p.displayName,
        profile_picture: p.picture
      }
    )
  },
  async USER_REVOKE_GRANT(ctx: ActionContext<UserState, RootState>, p: { serviceId: string | null, service: string, token: string | null }): Promise<void> {
    await restUser.revokeGrant(ctx.getters.partyId, p.service, p.serviceId, p.token)
  },
  async USER_UPLOAD_FILE(ctx: ActionContext<UserState, RootState>, p: { file: File }): Promise<{url: string}> {
    // TODO: Rename to "upload profile picture"
    return await restFile.profile(ctx.state.token, p.file, ctx.getters.partyId)
  },
  async USER_UPLOAD_FILE_GRANT(ctx: ActionContext<UserState, RootState>, p: { file: File }): Promise<{url: string}> {
    return await restFile.profileGrant(p.file, ctx.getters.partyId)
  },
  USER_SIGNUP(ctx: ActionContext<UserState, RootState>, p: { isCompetition?: boolean }): Promise<boolean | string> {
    return restUser.signup(
      ctx.getters.userToken,
      ctx.getters.partyId,
      ctx.state.name,
      ctx.state.displayName,
      ctx.state.countryId,
      ctx.state.email,
      ctx.state.profilePicture,
      ctx.state.profileSticker,
      p.isCompetition,
    )
      .then((user) => {
        ctx.commit('SET_USER_ID', user.id)
        ctx.commit('SET_USER_IS_SUBSCRIBED', true)
        if (user.access_token) {
          ctx.commit('SET_USER_TOKEN', user.access_token)
          localStorage.setItem(ctx.getters.userLocalAccessKey, user.access_token)
          localStorage.removeItem(ctx.getters.userLocalService) // no service, party only login
        }
        return true
      })
      .catch((err) => {
        if (err.response.data.error === 'DuplicateSignupError') {
          if (err.response.data.fields.includes('email')) {
            return 'errorDuplicateEmail'
          }
          if (err.response.data.fields.includes('display_name')) {
            return 'errorDuplicateUserName'
          }
        }
        return false
      })
  },
  async USER_LOGOUT(ctx: ActionContext<UserState, RootState>) {
    const userEmail = ctx.getters.userEmail
    try {
      await restUser.logout(ctx.state.token)
    } catch (err) {
      console.error('Logout error ignored', err)
    }
    if (ctx.state.service === 'apple') {
      await apple.unauthorize()
    }
    ctx.commit('SET_USER_TOKEN', '')
    localStorage.removeItem(ctx.getters.userLocalAccessKey)
    localStorage.removeItem(ctx.getters.userLocalService)
    cms.tracking.trackCustomer('logout_success', { email: userEmail })
  },
  async USER_LOGIN(ctx: ActionContext<UserState, RootState>, p: { service: string, artistIds: string[] }) {
    // TODO: Use the new service for Spotify
    ctx.commit('SET_USER_SERVICE', p.service)
    localStorage.setItem(ctx.getters.userLocalService, p.service)

    switch (p.service) {
      case 'spotify': {
        const url = new SpotifyClient().getLoginUrl(p.artistIds, ctx.getters.partyId)

        window.open(url, '_self')

        return true
      }
      case 'apple': {
        await ctx.dispatch('USER_LOGIN_APPLE')
        return true
      }
      default: {
        return false
      }
    }
  },
  async USER_LOGIN_APPLE(ctx: ActionContext<UserState, RootState>, silent: boolean = false) {
    let accessToken: string | null = null
    const grantToken = localStorage.getItem('party_grant_token')
    try {
      const musicToken = await apple.authorize()
      accessToken = await restUser.loginApple(ctx.getters.partyId, musicToken, grantToken)
    } catch (err) {
      if ((err as RestError).status === 401 || (err as RestError).status === 422) {
        // Invalid/expired MusicKit Token, log out MusicKit
        await apple.unauthorize()
      }
      // Silent mode for login attempt on startup
      if (!silent) {
        throw err
      }
    }
    if (accessToken) {
      ctx.commit('SET_USER_TOKEN', accessToken)
      ctx.commit('SET_USER_SERVICE', 'apple')
      localStorage.setItem(ctx.getters.userLocalAccessKey, accessToken)
      localStorage.setItem(ctx.getters.userLocalService, 'apple')
      // Load the current party/service user
      await ctx.dispatch('USER_INIT')
      cms.tracking.trackCustomer('login_success', { method: 'apple', email: ctx.getters.userEmail })
      // Apple SDK player needs to initialize here in case the user wasn't logged in on startup
      await ctx.dispatch('PLAYER_INIT')
    }
  },
  async USER_LOAD_SERVICE_TOKEN(ctx: ActionContext<UserState, RootState>): Promise<void> {
    let result: { token: string, expires_at: number }|null = null
    try {
      result = await restUser.getServiceToken(ctx.getters.userToken)
    } catch (err) {
      if ((err as RestError).status === 401) {
        // Clear all login state if token is invalid
        await ctx.dispatch('USER_LOGOUT')
        return
      }
      throw (err)
    }
    ctx.commit('SET_USER_SERVICE_TOKEN', result.token)
    ctx.commit('SET_USER_SERVICE_TOKEN_EXPIRES', result.expires_at)
  },
  async USER_LOAD_SERVICE_TOKEN_IF_NEEDED(ctx: ActionContext<UserState, RootState>): Promise<void> {
    if (ctx.getters.userServiceTokenIsExpiring()) {
      await ctx.dispatch('USER_LOAD_SERVICE_TOKEN')
    }
  },
  async USER_PARTY_LOAD(ctx: ActionContext<UserState, RootState>): Promise<void> {
    const user = await restUser.getParty(ctx.getters.userToken, ctx.getters.partyId)
    ctx.commit('SET_USER_IS_SUBSCRIBED', true)
    ctx.commit('SET_USER_ID', user.id)
    ctx.commit('SET_USER_NAME', user.real_name)
    ctx.commit('SET_USER_DISPLAY_NAME', user.display_name)
    ctx.commit('SET_USER_EMAIL', user.email)
    ctx.commit('SET_USER_PREMIUM', user.premium)
    ctx.commit('SET_USER_COUNTRY_ID', user.country)
    ctx.commit('SET_USER_ROLE', user.role)
    ctx.commit('SET_USER_TRACKS_VOTED', user.track_votes)
    ctx.commit('SET_USER_CAN_CHAT', user.can_chat)
    ctx.commit('SET_USER_POLL_ANSWERS', user.poll_answers)
    ctx.commit('SET_USER_QUIZ_ANSWERS', user.quiz_answers)
    ctx.commit('SET_USER_PROFILE_PICTURE', user.profile_picture || '')
    ctx.commit('SET_USER_SERVICE', user.service)
  },
  async USER_SYSTEM_LOAD(ctx: ActionContext<UserState, RootState>): Promise<void> {
    if (!ctx.state.token) {
      // Skip call entirely if token is missing
      return
    }
    const user = await restUser.getSystem(ctx.state.token)
    ctx.commit('SET_USER_NAME', user.real_name || '')
    ctx.commit('SET_USER_DISPLAY_NAME', user.display_name || '')
    ctx.commit('SET_USER_EMAIL', user.email || '')
    ctx.commit('SET_USER_COUNTRY_ID', user.country || '')
    ctx.commit('SET_USER_PROFILE_PICTURE', user.profile_picture || '')
    ctx.commit('SET_USER_PREMIUM', user.premium)
    ctx.commit('SET_USER_ROLE', user.role)
    ctx.commit('SET_USER_SERVICE', user.service)
  },
  async USERS_DELETE_ALL(ctx: ActionContext<UserState, RootState>): Promise<void> {
    return restUser.deleteAllUsers(ctx.getters.partyId)
  },
}

const getters: GetterTree<UserState, any> = {
  userIsAuthenticated: (state: UserState) => !!state.token,
  userId: (state: UserState) => state.id,
  userName: (state: UserState) => state.name,
  userService: (state: UserState) => state.service,
  userDisplayName: (state: UserState) => state.displayName,
  userEmail: (state: UserState) => state.email,
  userPremium: (state: UserState) => state.premium,
  userCountryId: (state: UserState) => state.countryId,
  userRole: (state: UserState) => state.role,
  userIsAdmin: (state: UserState) => state.role === 'admin',
  userIsHost: (state: UserState) => state.role === 'host',
  userProfileSticker: (state: UserState) => state.profileSticker,
  userProfilePicture: (state: UserState) => state.profilePicture,
  userToken: (state: UserState) => state.token,
  userLocalAccessKey: (state: UserState) => `${state.localKey}_access_key`,
  userLocalService: (state: UserState) => `${state.localKey}_service`,
  userIsSubscribed: (state: UserState) => state.isSubscribed,
  userServiceToken: (state: UserState) => state.serviceToken,
  userServiceTokenIsExpiring: (state: UserState) => () => {
    return state.token && state.service === 'spotify' && (new Date().getTime() + (1000 * 60 * 9)) > (state.serviceTokenExpires * 1000)
  },
  userTracksVoted: (state: UserState) => state.tracksVoted,
  userGrants: (state: UserState) => state.grants,
  userCanChat: (state: UserState) => state.canChat,
  userPollAnswers: (state: UserState) => state.pollAnswers,
  userQuizAnswers: (state: UserState) => state.quizAnswers,
  userById: (state: UserState) => (id: number) => state.users.find((u) => u.id === id),
}

export const userModule: Module<UserState, any> = {
  state: userState,
  mutations,
  actions,
  getters,
}
